import type { MvMTurnResult } from '../../hooks/useMvMBattleSocket'

/** One-line summary for the floating battle HUD (any action, including 0 damage). */
export function formatMvMTurnSummary(tr: MvMTurnResult | null | undefined): string {
  if (!tr) return ''
  const action = String(tr.action || '')
  if (action === 'SKIP') return 'Turn skipped'

  const parts: string[] = []
  const fx = Array.isArray(tr.effects) ? tr.effects : []

  if (action === 'REST' || fx.some((e) => e && typeof e === 'object' && (e as { type?: string }).type === 'rest')) {
    parts.push('Rest (+SP)')
  }
  if (action === 'MOVE_ONLY' && tr.damage <= 0) {
    parts.push('Move')
  }

  for (const raw of fx) {
    if (!raw || typeof raw !== 'object') continue
    const e = raw as { type?: string; name?: string; damageReflected?: number }
    if (e.type === 'ultimate') {
      const name = (raw as { name?: string }).name?.trim()
      parts.push(name ? `Ultimate: ${name}` : 'Ultimate')
    }
    if (e.type === 'evaded') parts.push('Evaded')
    if (e.type === 'reflected' && typeof e.damageReflected === 'number' && e.damageReflected > 0) {
      parts.push(`Reflected ${e.damageReflected}`)
    }
    if (e.type === 'watchtower_hit') parts.push('Watchtower hit')
    if (e.type === 'counterattack') {
      const dmg = Number((raw as { damage?: number }).damage)
      parts.push(dmg > 0 ? `Counterattack ${dmg}` : 'Counterattack')
    }
    if (e.type === 'elemental_placed') {
      const et = (raw as { elementalType?: string }).elementalType
      parts.push(et ? `Elemental: ${et}` : 'Elemental placed')
    }
  }

  if (tr.damage > 0) {
    parts.unshift(`${tr.isCrit ? 'CRIT! ' : ''}${tr.damage} damage`)
  } else if (action === 'BASE' && !fx.some((e) => e && typeof e === 'object' && (e as { type?: string }).type === 'evaded')) {
    parts.push('Attack')
  }

  if (action === 'ULTIMATE') {
    const hasUltFx = fx.some((e) => e && typeof e === 'object' && (e as { type?: string }).type === 'ultimate')
    if (!hasUltFx) parts.push('Ultimate')
  }

  if (tr.targetDied) parts.push('Target eliminated')
  if (tr.characterDied) parts.push('Unit defeated')

  const out = parts.filter(Boolean)
  const seen = new Set<string>()
  const dedup = out.filter((p) => (seen.has(p) ? false : (seen.add(p), true)))
  return dedup.join(' · ') || 'Action resolved'
}
